import { useState } from 'react';
import { Pencil, Trash2, ToggleLeft, ToggleRight, X, Save } from 'lucide-react';
import {
  EditCheckSpec,
  SEVERITY_CONFIG,
  TIMING_CONFIG,
  CHECK_TYPE_LABELS,
} from '../../constants/editCheckOptions';
import { checkSourceText, isDefaultRange } from '../../features/editChecks/dictionaryChecksDisplay';
import { displayProtocolReference } from '../../features/editChecks/protocolReferenceLabel';

interface EditCheckDetailProps {
  check: EditCheckSpec;
  onSave: (updated: EditCheckSpec) => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

export default function EditCheckDetail({
  check,
  onSave,
  onToggleActive,
  onDelete,
}: EditCheckDetailProps) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState<EditCheckSpec>(check);

  const handleStartEdit = () => {
    setDraft({ ...check });
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setDraft(check);
  };

  const handleSave = () => {
    onSave(draft);
    setEditing(false);
  };

  const severityCfg = SEVERITY_CONFIG[check.severity];
  const timingCfg = TIMING_CONFIG[check.timing];

  if (editing) {
    return (
      <div className="bg-gray-50 border-t border-gray-200 p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Rule */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Rule</label>
            <textarea
              value={draft.rule}
              onChange={(e) => setDraft({ ...draft, rule: e.target.value })}
              rows={2}
              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            />
          </div>

          {/* Technical Rule */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Technical Rule</label>
            <textarea
              value={draft.technicalRule}
              onChange={(e) => setDraft({ ...draft, technicalRule: e.target.value })}
              rows={2}
              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            />
          </div>

          {/* Query Message */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Query Message</label>
            <textarea
              value={draft.queryMessage}
              onChange={(e) => setDraft({ ...draft, queryMessage: e.target.value })}
              rows={2}
              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            />
          </div>

          {/* Severity */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Severity</label>
            <select
              value={draft.severity}
              onChange={(e) =>
                setDraft({ ...draft, severity: e.target.value as EditCheckSpec['severity'] })
              }
              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            >
              {Object.entries(SEVERITY_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>
                  {cfg.label}
                </option>
              ))}
            </select>
          </div>

          {/* Timing */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Timing</label>
            <select
              value={draft.timing}
              onChange={(e) =>
                setDraft({ ...draft, timing: e.target.value as EditCheckSpec['timing'] })
              }
              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            >
              {Object.entries(TIMING_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>
                  {cfg.label}
                </option>
              ))}
            </select>
          </div>

          {/* Check Type */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Check Type</label>
            <select
              value={draft.checkType}
              onChange={(e) => setDraft({ ...draft, checkType: e.target.value })}
              className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            >
              {Object.entries(CHECK_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Save / Cancel */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <Save className="h-3.5 w-3.5" />
            Save
          </button>
          <button
            onClick={handleCancelEdit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-gray-600 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border-t border-gray-200 p-4">
      <div className="grid grid-cols-3 gap-4">
        {/* Left: rules & query */}
        <div className="col-span-2 space-y-3">
          {/* Rule */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Rule
            </h4>
            <p className="text-sm text-gray-800">{check.rule}</p>
          </div>

          {/* Technical Rule */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Technical Rule
            </h4>
            <pre className="text-xs font-mono bg-gray-100 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap text-gray-700 overflow-x-auto">
              {check.technicalRule}
            </pre>
          </div>

          {/* Query Message */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Query Message
            </h4>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              {check.queryMessage}
            </div>
          </div>
        </div>

        {/* Right: metadata */}
        <div className="space-y-3">
          {/* Source (D7) */}
          {checkSourceText(check) && (
            <div>
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Source
              </h4>
              <p className={isDefaultRange(check) ? 'text-sm text-amber-800 font-medium' : 'text-sm text-teal-800'}>{checkSourceText(check)}</p>
            </div>
          )}

          {/* Protocol Reference */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              {isDefaultRange(check) ? 'Reference (a default, not the protocol)' : 'Protocol Reference'}
            </h4>
            <p className="text-sm text-gray-700">{displayProtocolReference(check.protocolReference) || 'None'}</p>
          </div>

          {/* Timing */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Timing
            </h4>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${timingCfg.bgClass}`}
            >
              {timingCfg.label}
            </span>
          </div>

          {/* Severity */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Severity
            </h4>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${severityCfg.bgClass}`}
            >
              {severityCfg.label}
            </span>
          </div>

          {/* Related Fields */}
          {check.relatedFields && check.relatedFields.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Related Fields
              </h4>
              <ul className="space-y-0.5">
                {check.relatedFields.map((f) => (
                  <li key={f} className="text-xs text-gray-600 font-mono">
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Related Forms */}
          {check.relatedForms && check.relatedForms.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Related Forms
              </h4>
              <ul className="space-y-0.5">
                {check.relatedForms.map((f) => (
                  <li key={f} className="text-xs text-gray-600">
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Active toggle */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Status
            </h4>
            <button
              onClick={onToggleActive}
              className="inline-flex items-center gap-1.5 text-sm"
            >
              {check.active ? (
                <>
                  <ToggleRight className="h-5 w-5 text-green-600" />
                  <span className="text-green-700 font-medium">Active</span>
                </>
              ) : (
                <>
                  <ToggleLeft className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-500 font-medium">Inactive</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom actions */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-200">
        <button
          onClick={handleStartEdit}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </button>

        {confirmDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-600">Delete this check?</span>
            <button
              onClick={onDelete}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              Confirm
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
